import React, { useEffect, useState } from "react";
import {
    ChonkyActions,
    FileBrowser,
    FileContextMenu,
    FileList,
    FileNavbar,
    FileToolbar,
} from "chonky";
import {
    Box,
    Container,
    Typography,
    Paper,
    Card,
    CardContent,
    Tab,
    Tabs,
    Divider,
    Button,
    CircularProgress,
    Backdrop,
    IconButton,
    Chip,
    Tooltip,
    useTheme,
    alpha,
    Alert,
    Snackbar,
    Grid,
    Breadcrumbs,
    Link,
    LinearProgress
} from "@mui/material";
import EnhancedFolderUpload from "../Folder upload/FolderUpload";
import { ApiDetails, KEY } from "../../dummyData";
import CryptoJS from "crypto-js";

// Icons
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import FolderIcon from '@mui/icons-material/Folder';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import DeleteIcon from '@mui/icons-material/Delete';
import GetAppIcon from '@mui/icons-material/GetApp';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import RefreshIcon from '@mui/icons-material/Refresh';
import StorageIcon from '@mui/icons-material/Storage';
import HomeIcon from '@mui/icons-material/Home';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import RecentActorsIcon from '@mui/icons-material/RecentActors';

export const MyFileBrowser  = (props) => {
    const theme = useTheme();
    const [activeTab, setActiveTab] = useState(0);
    const [apiResponse, setApiResponse] = useState([]);
    const [folderViews, setFolderViews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPath, setCurrentPath] = useState("/");
    const [pathHistory, setPathHistory] = useState([{ id: "root", name: "Home", path: "/" }]);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });
    const [folderState, setFolderState] = useState({});
    const [refresh, setRefresh] = useState(true);

    // Get landlord ID from localStorage
    const [landlordId] = useState(() => {
        try {
            const myMapDetailsDecrypted = CryptoJS.AES.decrypt(
                localStorage.getItem("myMap"),
                KEY
            );
            const originalText = myMapDetailsDecrypted.toString(CryptoJS.enc.Utf8);
            return JSON.parse(originalText)[0][1];
        } catch (error) {
            console.error("Error decrypting data:", error);
            return null;
        }
    });

    // Custom file actions for Chonky
    const myFileActions = [
        ChonkyActions.UploadFiles,
        ChonkyActions.DownloadFiles,
        ChonkyActions.DeleteFiles,
    ];

    // Fetch files when component mounts or refresh changes
    useEffect(() => {
        fetchFiles(currentPath);
    }, [refresh, currentPath]);

    // Main file fetching function
    const fetchFiles = (path = "/") => {
        setLoading(true);

        fetch(ApiDetails + "pegasus/visionary/files/retrieve/backup", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "token": "8d5b2e7c-36f4-4b45-b79b-61fc60eb905d"
            },
            body: JSON.stringify({
                landlord_id: landlordId,
                path: path === "/" ? undefined : path,
            }),
        })
            .then(async (response) => {
                const data = await response.json();
                if (response.status === 200) {
                    // Enhance file data with icons and additional attributes
                    const enhancedFiles = data.files.map(file => ({
                        ...file,
                        iconComponent: file.isDir ? FolderIcon : InsertDriveFileIcon,
                        color: file.isDir ? theme.palette.primary.main : getFileColor(file.name),
                        modDate: file.modDate ? new Date(file.modDate).toLocaleString() : "Unknown",
                    }));

                    setApiResponse(enhancedFiles);
                    setFolderViews(enhancedFiles);
                    setLoading(false);

                    // Update path history if navigating to a new path
                    if (path !== currentPath) {
                        setCurrentPath(path);
                        updatePathHistory(path);
                    }
                } else if (response.status === 404) {
                    setLoading(false);
                    setSnackbar({
                        open: true,
                        message: "No backup files found. Upload some files to get started.",
                        severity: "info"
                    });
                } else {
                    setLoading(false);
                    setSnackbar({
                        open: true,
                        message: "Failed fetching backup files. Please try again.",
                        severity: "error"
                    });
                }
            })
            .catch((err) => {
                console.error(err);
                setLoading(false);
                setSnackbar({
                    open: true,
                    message: "Something went wrong. Please contact customer care for assistance.",
                    severity: "error"
                });
            });
    };

    // Update path history when navigating through folders
    const updatePathHistory = (path) => {
        const pathParts = path.split('/').filter(part => part);
        const newHistory = [{ id: "root", name: "Home", path: "/" }];

        let currentPath = "";

        pathParts.forEach(part => {
            currentPath += `/${part}`;
            newHistory.push({
                id: part,
                name: part,
                path: currentPath
            });
        });

        setPathHistory(newHistory);
    };

    // Navigate to a specific path in history
    const navigateToPath = (path) => {
        setCurrentPath(path);
        fetchFiles(path);
    };

    // Handle file actions (open, delete, download)
    const handleAction = (data) => {
        const action = data.action.id;
        const file = data.state.selectedFiles[0];

        if (!file) return;

        const fileId = file.id;

        // Find the file in our data
        const selectedFile = apiResponse.find(f => f.id === fileId);
        if (!selectedFile) return;

        switch (action) {
            case "open_files":
                if (selectedFile.isDir) {
                    navigateToPath(selectedFile.path);
                }
                break;

            case "delete_files":
                deleteFile(selectedFile.path);
                break;

            case "download_files":
                downloadFile(selectedFile.path);
                break;

            default:
                break;
        }
    };

    // Delete file or folder
    const deleteFile = (path) => {
        setLoading(true);

        fetch(ApiDetails + "pegasus/visionary/files/delete/file", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "token": "8d5b2e7c-36f4-4b45-b79b-61fc60eb905d"
            },
            body: JSON.stringify({
                path: path,
            }),
        })
            .then(async (response) => {
                if (response.status === 200) {
                    setSnackbar({
                        open: true,
                        message: "File deleted successfully",
                        severity: "success"
                    });
                    setRefresh(!refresh);
                } else {
                    setSnackbar({
                        open: true,
                        message: "Failed to delete file. Please try again.",
                        severity: "error"
                    });
                    setLoading(false);
                }
            })
            .catch((err) => {
                console.error(err);
                setLoading(false);
                setSnackbar({
                    open: true,
                    message: "Something went wrong. Please contact customer care for assistance.",
                    severity: "error"
                });
            });
    };

    // Download file
    const downloadFile = (path) => {
        setLoading(true);
        let fileName;

        fetch(ApiDetails + "pegasus/visionary/files/download/file", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "token": "8d5b2e7c-36f4-4b45-b79b-61fc60eb905d"
            },
            body: JSON.stringify({
                path: path,
            }),
        })
            .then(async (res) => {
                if (res.status === 200) {
                    // Extract filename from headers
                    for (let pair of res.headers.entries()) {
                        if (pair[0] === "content-disposition") {
                            fileName = pair[1];
                        }
                    }

                    setSnackbar({
                        open: true,
                        message: "File download has started",
                        severity: "success"
                    });

                    // Create download link
                    const blob = await res.blob();
                    const href = window.URL.createObjectURL(blob);
                    const link = document.createElement("a");
                    link.href = href;
                    link.setAttribute("download", fileName);
                    document.body.appendChild(link);
                    link.click();

                    // Clean up
                    document.body.removeChild(link);
                    URL.revokeObjectURL(href);
                    setLoading(false);
                } else {
                    setSnackbar({
                        open: true,
                        message: "Failed downloading file. Please try again.",
                        severity: "error"
                    });
                    setLoading(false);
                }
            })
            .catch((err) => {
                console.error(err);
                setLoading(false);
                setSnackbar({
                    open: true,
                    message: "Something went wrong. Please contact customer care for assistance.",
                    severity: "error"
                });
            });
    };

    // Handle folder open state
    const handleFolderOpen = (folderId) => {
        setFolderState((prev) => ({
            ...prev,
            [folderId]: !prev[folderId],
        }));
    };

    const getFolderState = (folderId) => folderState[folderId] || false;

    // Get color based on file extension
    const getFileColor = (fileName) => {
        const extension = fileName.split('.').pop().toLowerCase();

        if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg'].includes(extension)) {
            return theme.palette.error.main; // Red for images
        } else if (['pdf'].includes(extension)) {
            return theme.palette.warning.main; // Orange for PDFs
        } else if (['doc', 'docx', 'txt', 'rtf'].includes(extension)) {
            return theme.palette.info.main; // Blue for documents
        } else if (['xls', 'xlsx', 'csv'].includes(extension)) {
            return theme.palette.success.main; // Green for spreadsheets
        } else if (['zip', 'rar', '7z', 'tar', 'gz'].includes(extension)) {
            return theme.palette.secondary.main; // Purple for archives
        }

        return theme.palette.text.secondary; // Default color
    };

    // Handle tab change
    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    // Close snackbar
    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    return (
        <Container maxWidth="xl" sx={{ py: 3 }}>
            {/* Snackbar for notifications */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                    variant="filled"
                    elevation={6}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>

            {/* Backdrop during loading */}
            <Backdrop
                sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
                open={loading}
            >
                <CircularProgress color="inherit" />
            </Backdrop>

            {/* Header and Tabs */}
            <Card
                elevation={0}
                sx={{
                    mb: 3,
                    borderRadius: 2,
                    overflow: 'hidden',
                    border: `1px solid ${theme.palette.divider}`
                }}
            >
                <Box
                    sx={{
                        p: 3,
                        bgcolor: theme.palette.primary.main,
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2
                    }}
                >
                    <StorageIcon fontSize="large" />
                    <Box>
                        <Typography variant="h5" fontWeight="bold">
                            File Manager
                        </Typography>
                        <Typography variant="body2">
                            Upload, download, and manage your backup files
                        </Typography>
                    </Box>
                </Box>

                <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    variant="fullWidth"
                    sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}
                >
                    <Tab
                        icon={<RecentActorsIcon />}
                        label="File Browser"
                        iconPosition="start"
                    />
                    <Tab
                        icon={<UploadFileIcon />}
                        label="Upload Files"
                        iconPosition="start"
                    />
                </Tabs>
            </Card>

            {/* Main Content */}
            <Box>
                {/* Tab 1: File Browser */}
                {activeTab === 0 && (
                    <Card
                        elevation={0}
                        sx={{
                            borderRadius: 2,
                            border: `1px solid ${theme.palette.divider}`,
                            height: 'calc(100vh - 260px)',
                            display: 'flex',
                            flexDirection: 'column'
                        }}
                    >
                        {/* Path breadcrumbs and actions */}
                        <Box
                            sx={{
                                p: 2,
                                borderBottom: `1px solid ${theme.palette.divider}`,
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                            }}
                        >
                            <Breadcrumbs
                                separator={<NavigateNextIcon fontSize="small" />}
                                aria-label="file path breadcrumb"
                            >
                                {pathHistory.map((item, index) => {
                                    const isLast = index === pathHistory.length - 1;
                                    return isLast ? (
                                        <Typography
                                            key={item.id}
                                            color="text.primary"
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                fontWeight: 'medium'
                                            }}
                                        >
                                            {index === 0 ? <HomeIcon fontSize="small" sx={{ mr: 0.5 }} /> : null}
                                            {item.name}
                                        </Typography>
                                    ) : (
                                        <Link
                                            key={item.id}
                                            color="inherit"
                                            component="button"
                                            onClick={() => navigateToPath(item.path)}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                textDecoration: 'none'
                                            }}
                                        >
                                            {index === 0 ? <HomeIcon fontSize="small" sx={{ mr: 0.5 }} /> : null}
                                            {item.name}
                                        </Link>
                                    );
                                })}
                            </Breadcrumbs>

                            <Box>
                                <Tooltip title="Refresh Files">
                                    <IconButton onClick={() => setRefresh(!refresh)}>
                                        <RefreshIcon />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Box>

                        {/* Empty state */}
                        {folderViews.length === 0 && !loading && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    height: '100%',
                                    p: 3
                                }}
                            >
                                <FolderIcon sx={{ fontSize: 60, color: alpha(theme.palette.primary.main, 0.3), mb: 2 }} />
                                <Typography variant="h6" gutterBottom>
                                    No files found in this location
                                </Typography>
                                <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
                                    This folder is empty. Upload files or navigate to a different location.
                                </Typography>
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    startIcon={<CloudUploadIcon />}
                                    onClick={() => setActiveTab(1)}
                                >
                                    Upload Files
                                </Button>
                            </Box>
                        )}

                        {/* File Browser */}
                        {folderViews.length > 0 && (
                            <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
                                <FileBrowser
                                    files={folderViews}
                                    folderChain={[{id: "xcv", name: "Home", isDir: true}]}
                                    onFolderOpen={handleFolderOpen}
                                    getFolderIsOpen={getFolderState}
                                    fileActions={myFileActions}
                                    onFileAction={handleAction}
                                    disableDefaultFileActions={false}
                                    darkMode={theme.palette.mode === 'dark'}
                                >
                                    <FileNavbar />
                                    <FileToolbar />
                                    <FileList />
                                    <FileContextMenu />
                                </FileBrowser>
                            </Box>
                        )}
                    </Card>
                )}

                {/* Tab 2: Upload Files */}
                {activeTab === 1 && (
                    <EnhancedFolderUpload
                        refresh={() => {
                            setRefresh(!refresh);
                            setActiveTab(0); // Switch back to file browser after upload
                        }}
                        snackBar={(message) => {
                            setSnackbar({
                                open: true,
                                message: message.text,
                                severity: message.type,
                            });
                        }}
                        topBar={props.topBar}
                    />
                )}
            </Box>
        </Container>
    );
};